import { activeLoader, setLicenseExpireFlag, setUnregisterdUserMessage } from "./view.actions";
import { narrativeBoardService } from "../services/narrative-board.service";
import { NarrativeBoardOption, NarrativeBoardCard, NarrativeBoardComment, GetCommentInfo, AddCommentInfo, EditCommentInfo, DeleteRequestInfo } from "../modal/narrative-board";
import { NARRATIVEBOARDCONFIG } from "../components/narrative-board/narrative-board-config";
import { UserConfigModal } from "../modal/notification";
import { CONFIG } from "../helper/config";

export const narrativeBoardActions = {
    SET_NARRATIVE_BOARD_MESSAGE: 'SET_NARRATIVE_BOARD_MESSAGE',
    SET_NARRATIVE_BOARD_OPTIONS: 'SET_NARRATIVE_BOARD_OPTIONS',
    SET_NARRATIVE_BOARD_CARDS: 'SET_NARRATIVE_BOARD_CARDS',
    SET_NARRATIVE_BOARD_TEAM_MEMBERS: 'SET_NARRATIVE_BOARD_TEAM_MEMBERS',
    UPDATE_NARRATIVE_BOARD_CARDS: 'UPDATE_NARRATIVE_BOARD_CARDS',
    EMPTY_NARRATIVE_BOARD_CARDS: 'EMPTY_NARRATIVE_BOARD_CARDS',
    SET_NARRATIVE_BOARD_CARD_HISTORY: 'SET_NARRATIVE_BOARD_CARD_HISTORY',
    UPDATE_COMMENTS: 'UPDATE_COMMENTS',
    SHOW_LOADER: 'SHOW_LOADER',
};

/**
* update narrative board loader state to store
*/
export const setLoaderState = (show_loader: boolean) => (dispatch: (arg0: { type: string; payload: { show_loader: boolean; }; }) => void) => {
    dispatch({
        type: narrativeBoardActions.SHOW_LOADER,
        payload: { show_loader },
    });
};

/**
* update narrative board message state to  store
*/
export const setNarrativeBoardMessage = (msg: string) => (dispatch: (arg0: { type: string; payload: { narrative_board_msg: string; }; }) => void) => {
    dispatch({
        type: narrativeBoardActions.SET_NARRATIVE_BOARD_MESSAGE,
        payload: { narrative_board_msg: msg },
    });
};

/**
 * Calls getTeams service to get the teams and updates the store with the result.
 * @param {any} info - The request info object to be passed to the service.
 * @param {string} port_no - The port number to be passed to the service.
 * @param {string} auth_token - The authentication token to be passed to the service.
 */
export const getTeams = (info: any, port_no: string, auth_token: string) => (dispatch: (arg0: any) => void) => {
    dispatch(activeLoader(true));
    narrativeBoardService.getTeams(info, port_no, auth_token).then((result) => {
        dispatch(setTeams(result));
        dispatch(activeLoader(false));
    }).catch((error: any) => {
        dispatch(handleCatch(error));
    })
}


/**
 * Updates the store with the given narrative board options.
 * @param {NarrativeBoardOption[]} narrative_board_options - The array of narrative board options to be set in the store.
 */
export const setTeams = (narrative_board_options: NarrativeBoardOption[]) => (dispatch: (arg0: { type: string; payload: { narrative_board_options: NarrativeBoardOption[]; }; }) => void) => {
    dispatch({
        type: narrativeBoardActions.SET_NARRATIVE_BOARD_OPTIONS,
        payload: { narrative_board_options: narrative_board_options },
    });
};

/**
 * Calls addCardToNarrativeBoard service to add a card to the narrative board and updates the store with the result.
 * @param {any} info - The request info object to be passed to the service.
 * @param {string} port_no - The port number to be passed to the service.
 * @param {string} auth_token - The authentication token to be passed to the service.
 */
export const addCardToNarrativeBoard = (info: any, port_no: string, auth_token: string) => (dispatch: (arg0: any) => void) => {
    dispatch(activeLoader(true));
    narrativeBoardService.addCardToNarrativeBoard(info, port_no, auth_token).then((result) => {
        dispatch(setNarrativeBoardMessage(result));
        dispatch(activeLoader(false));
    }).catch((error: any) => {
        dispatch(handleCatch(error));
    })
}

/**
 * Fetches narrative board cards and team members and updates the store with the results.
 * 
 * @param {DeleteRequestInfo} info - The request info containing tenant and team identifiers.
 * @param {number} insight_length - The length of the insight to be fetched.
 * @param {string} search_string - The search query string for filtering results.
 * @param {string} auth_token - The authentication token for API requests.
 * @param {boolean} [is_loader_active=true] - Flag to indicate if a loader should be displayed during the request.
 * @param {any} [id] - Optional identifier for specific narrative board cards retrieval.
 * 
 * Dispatches actions to update the store with fetched narrative board cards and team members.
 * Handles any errors that occur during the fetching process.
 */
export const getNarrativeBoardCardsAndTeamMembers = (info: DeleteRequestInfo, insight_length: number, search_string: string, auth_token: string, is_loader_active = true, id?: any) => (dispatch: (arg0: any) => void) => {
    if (is_loader_active) {
        dispatch(activeLoader(true));
    }
    Promise.all([
        narrativeBoardService.getNarrativeBoardCards(info, insight_length, search_string, auth_token, id),
        narrativeBoardService.getNarrativeBoardTeamMembers(info.tenant_id, info.team_id, auth_token),
    ]).then((results) => {
        dispatch(setNarrativeBoardCards(results[0]));
        dispatch(setNarrativeBoardTeamMembers(results[1]));
        dispatch(activeLoader(false));
    })
        .catch((error) => {
            dispatch(handleCatch(error, true));
        });
}

/**
 * Fetches narrative board cards based on provided parameters and updates the store.
 *
 * @param {DeleteRequestInfo} info - The request info containing tenant and team identifiers.
 * @param {number} insight_length - The length of the insight to be fetched.
 * @param {string} search_string - The search query string for filtering results.
 * @param {string} auth_token - The authentication token for API requests.
 * @param {boolean} [is_loader_active=true] - Flag to indicate if a loader should be displayed during the request.
 * @param {any} [id] - Optional identifier for specific narrative board cards retrieval.
 *
 * Dispatches actions to update the store with fetched narrative board cards.
 * Handles any errors that occur during the fetching process.
 */
export const getNarrativeBoardCards = (info: DeleteRequestInfo, insight_length: number, search_string: string, auth_token: string, is_loader_active = true, id?: any) => (dispatch: (arg0: any) => void) => {
    if (is_loader_active) {
        dispatch(activeLoader(true));
    }
    narrativeBoardService.getNarrativeBoardCards(info, insight_length, search_string, auth_token, id).then((result) => {
        dispatch(setNarrativeBoardCards(result));
        dispatch(activeLoader(false));
    }).catch((error: any) => {
        dispatch(handleCatch(error, true, true));
    })
}

/**
 * Fetches team members for a given tenant and team and updates the store with the results.
 *
 * @param {string} tenant_id - The tenant identifier.
 * @param {string} team_id - The team identifier.
 * @param {string} auth_token - The authentication token for API requests.
 * @param {string} port_no - The port number to append to the URL.
 * @param {boolean} [is_loader_active=true] - Flag to indicate if a loader should be displayed during the request.
 * 
 * Dispatches actions to update the store with fetched team members.
 * Handles any errors that occur during the fetching process.
 */
export const getNarrativeBoardTeamMembers = (tenant_id: string, team_id: string, auth_token: string, port_no: string, is_loader_active = true) => (dispatch: (arg0: any) => void) => {
    if (is_loader_active) {
        dispatch(activeLoader(true));
    }
    narrativeBoardService.getNarrativeBoardTeamMembers(tenant_id, team_id, auth_token).then(result => {
        dispatch(setNarrativeBoardTeamMembers(result));
        dispatch(activeLoader(false));
    }).catch((error: any) => {
        dispatch(handleCatch(error, true))
    })
}

/**
 * Resets the narrative board cards to an empty array in the store.
 *
 * @return {any} The action object with type and payload to be dispatched.
 */
export const emptyNarrativeBoardCards = () => (dispatch: (arg0: any) => void) => {
    dispatch({
        type: narrativeBoardActions.EMPTY_NARRATIVE_BOARD_CARDS,
        payload: { narrative_board_cards: [] },
    });
}

/**
 * Updates the narrative board cards in the store with the provided array of cards.
 * 
 * @param {NarrativeBoardCard[]} narrative_board_cards - The array of narrative board cards to be stored.
 * @return {any} The action object with type and payload to be dispatched.
 */
export const setNarrativeBoardCards = (narrative_board_cards: NarrativeBoardCard[]) => (dispatch: (arg0: { type: string; payload: { narrative_board_cards: NarrativeBoardCard[]; }; }) => void) => {
    dispatch({
        type: narrativeBoardActions.SET_NARRATIVE_BOARD_CARDS,
        payload: { narrative_board_cards: narrative_board_cards },
    });
};

/**
 * Updates the store with the provided narrative board team members.
 *
 * @param {any} narrative_board_team_members - The team members to be stored in the narrative board.
 */
export const setNarrativeBoardTeamMembers = (narrative_board_team_members: any) => (dispatch: (arg0: any) => void) => {
    dispatch({
        type: narrativeBoardActions.SET_NARRATIVE_BOARD_TEAM_MEMBERS,
        payload: { narrative_board_team_members: narrative_board_team_members },
    })
}

/**
 * Deletes a card from the narrative board and updates the store with the new list of cards.
 *
 * @param {number} insight_card_id - The identifier of the card to be deleted.
 * @param {DeleteRequestInfo} info - The request info object containing tenant and team identifiers.
 * @param {string} auth_token - The authentication token for API requests.
 *
 * Calls the deleteNarrativeBoardCard service to delete the card from the narrative board and updates the store with the result.
 * Dispatches actions to update the store with the new list of cards and show a success message.
 * Handles any errors that occur during the deletion process.
 */
export const deleteNarrativeBoardCard = (insight_card_id: number, info: DeleteRequestInfo, auth_token: string) => (dispatch: (arg0: any) => void) => {
    dispatch(activeLoader(true));
    narrativeBoardService.deleteNarrativeBoardCard(insight_card_id, info, auth_token).then((result) => {
        dispatch(updateNarrativeBoardCards(insight_card_id));
        dispatch(setNarrativeBoardMessage(NARRATIVEBOARDCONFIG.DELETESUCCESSMESSAGE));
        dispatch(activeLoader(false));
    }).catch((error: any) => {
        dispatch(handleCatch(error));
    })
}

/**
 * Updates the store with the new list of cards after a card has been deleted or updated.
 *
 * @param {number} insight_card_id - The identifier of the card that was deleted or updated.
 *
 * Dispatches an action to update the store with the new list of cards.
 */
export const updateNarrativeBoardCards = (insight_card_id: number) => (dispatch: (arg0: { type: string; payload: { insight_card_id: number; }; }) => void) => {
    dispatch({
        type: narrativeBoardActions.UPDATE_NARRATIVE_BOARD_CARDS,
        payload: { insight_card_id: insight_card_id },
    });
};

/**
 * Fetches the narrative board card history for a specific card and updates the store with the results.
 *
 * @param {number} insight_card_id - The identifier of the card whose history is to be fetched.
 * @param {number} insight_length - The length of the insight history to be retrieved.
 * @param {any} teams_context - The context of the teams, including necessary identifiers.
 * @param {string} auth_token - The authentication token for API requests.
 *
 * Dispatches an action to update the store with the fetched narrative board card history.
 * Handles any errors that occur during the fetching process.
 */
export const getNarrativeBoardCardHistory = (insight_card_id: number, insight_length: number, teams_context: any, auth_token) => (dispatch: (arg0: any) => void) => {
    narrativeBoardService.getNarrativeBoardCardHistory(insight_card_id, insight_length, teams_context, auth_token).then((result) => {
        dispatch(setNarrativeBoardCardHistory(result, insight_card_id));
    }).catch((error: any) => {
        dispatch(handleCatch(error, false));
    })
}

/**
 * Updates the store with the narrative board card history for a specific card.
 *
 * @param {NarrativeBoardCard[]} narrative_board_card_history - The narrative board card history to be updated in the store.
 * @param {number} insight_card_id - The identifier of the card whose history is to be updated.
 *
 * Dispatches an action to update the store with the given narrative board card history.
 */
export const setNarrativeBoardCardHistory = (narrative_board_card_history: NarrativeBoardCard[], insight_card_id: number) =>
    (dispatch: (arg0: { type: string; payload: { narrative_board_card_history: NarrativeBoardCard[]; insight_card_id: number }; }) => void) => {
        dispatch({
            type: narrativeBoardActions.SET_NARRATIVE_BOARD_CARD_HISTORY,
            payload: { narrative_board_card_history: narrative_board_card_history, insight_card_id: insight_card_id },
        });
    };

/**
 * Updates the store with the given narrative board comments for a specific card.
 *
 * @param {NarrativeBoardComment[]} comments - The narrative board comments to be updated in the store.
 * @param {number} nb_insight_id - The identifier of the card whose comments are to be updated.
 * @param {GetCommentInfo} nb_card - The card whose comments are to be updated.
 *
 * Dispatches an action to update the store with the given narrative board comments.
 */
export const updateComments = (comments: NarrativeBoardComment[], nb_insight_id: number, nb_card: GetCommentInfo) =>
    (dispatch: (arg0: { type: string; payload: { comments: NarrativeBoardComment[]; nb_insight_id: number, nb_card: GetCommentInfo }; }) => void) => {
        dispatch({
            type: narrativeBoardActions.UPDATE_COMMENTS,
            payload: { comments, nb_insight_id, nb_card },
        });
    };

/**
 * Fetches comments for a specific narrative board card and updates the store.
 *
 * @param {GetCommentInfo} info - Information required to fetch comments, including identifiers.
 * @param {number} nb_insight_id - The identifier of the card whose comments are to be fetched.
 * @param {string} auth_token - The authentication token for API requests.
 *
 * Dispatches an action to update the store with the fetched comments.
 * Handles any errors that occur during the fetching process.
 */
export const getComments = (info: GetCommentInfo, nb_insight_id: number, auth_token: string) => (dispatch: (arg0: any) => void) => {
    narrativeBoardService.getComments(info, auth_token).then((result) => {
        dispatch(updateComments(result, nb_insight_id, info));
    }).catch((error: any) => {
        dispatch(handleCatch(error, false));
    })
}

/**
 * Adds a comment to the narrative board and fetches the updated comments list.
 *
 * Calls the addComment service to add a comment to the narrative board and then 
 * fetches the updated comments list for a specific card.
 *
 * @param {AddCommentInfo} add_comment_info - Information required to add the comment, 
 * including the comment content and related identifiers.
 * @param {GetCommentInfo} get_comment_info - Information required to fetch the updated 
 * comments list, including card identifiers.
 * @param {number} nb_insight_id - The identifier of the card to which the comment is being added.
 * @param {string} auth_token - The authentication token for API requests.
 *
 * Dispatches an action to update the store with the fetched comments.
 * Handles any errors that occur during the process.
 */
export const addComment = (add_comment_info: AddCommentInfo, get_comment_info: GetCommentInfo, nb_insight_id: number, auth_token: string) => (dispatch: (arg0: any) => void) => {
    narrativeBoardService.addComment(add_comment_info, auth_token).then((result) => {
        dispatch(getComments(get_comment_info, nb_insight_id, auth_token));
    }).catch((error: any) => {
        dispatch(handleCatch(error, false));
    })
}

/**
 * Edits a comment on the narrative board and fetches the updated comments list.
 *
 * Calls the editComment service to edit a comment on the narrative board and then 
 * fetches the updated comments list for a specific card.
 *
 * @param {EditCommentInfo} info - Information required to edit the comment, 
 * including the comment content and related identifiers.
 * @param {GetCommentInfo} get_comment_info - Information required to fetch the updated 
 * comments list, including card identifiers.
 * @param {number} nb_insight_id - The identifier of the card on which the comment is being edited.
 * @param {string} auth_token - The authentication token for API requests.
 *
 * Dispatches an action to update the store with the fetched comments.
 * Handles any errors that occur during the process.
 */
export const editComment = (info: EditCommentInfo, get_comment_info: GetCommentInfo, nb_insight_id: number, auth_token: string) => (dispatch: (arg0: any) => void) => {
    narrativeBoardService.editComment(info, auth_token).then((result) => {
        dispatch(getComments(get_comment_info, nb_insight_id, auth_token));
    }).catch((error: any) => {
        dispatch(handleCatch(error, false));
    })
}

/**
 * Deletes a comment on the narrative board and fetches the updated comments list.
 *
 * Calls the deleteComment service to delete a comment on the narrative board and then 
 * fetches the updated comments list for a specific card.
 *
 * @param {number} comment_id - The identifier of the comment being deleted.
 * @param {UserConfigModal} user_config_keys - Keys required for deleting a comment from the narrative board.
 * @param {GetCommentInfo} get_comment_info - Information required to fetch the updated comments list, including card identifiers.
 * @param {number} nb_insight_id - The identifier of the card on which the comment is being deleted.
 * @param {string} auth_token - The authentication token for API requests.
 *
 * Dispatches an action to update the store with the fetched comments.
 * Handles any errors that occur during the process.
 */
export const deleteComment = (comment_id: number, user_config_keys: UserConfigModal, get_comment_info: GetCommentInfo, nb_insight_id: number, auth_token: string) => (dispatch: (arg0: any) => void) => {
    narrativeBoardService.deleteComment(comment_id, user_config_keys, auth_token).then((result) => {
        dispatch(getComments(get_comment_info, nb_insight_id, auth_token));
    }).catch((error: any) => {
        dispatch(handleCatch(error, false));
    })
}

/**
 * Handles errors that occur during the fetching process.
 *
 * @param {any} error - The error to be handled.
 * @param {boolean} [is_loader=true] - Flag to indicate if a loader should be displayed during the request.
 * @param {boolean} [is_loader_state] - Flag to indicate if the loader state should be updated.
 *
 * Dispatches actions to update the store with the error message.
 * Handles any errors that occur during the fetching process.
 */
const handleCatch = (error: any, is_loader: boolean = true, is_loader_state?: boolean) => (dispatch: (arg0: any) => void) => {
    if (is_loader) {
        dispatch(activeLoader(false));
    }
    if (is_loader_state) {
        dispatch(setLoaderState(false));
    }
    console.error(error);
    if(error.demo){
        dispatch(setUnregisterdUserMessage(error.demo , error.msg))
    }else if (error.expiry) {
        dispatch(setLicenseExpireFlag(true));
    } else {
        error = typeof error === 'string' ? error : CONFIG.API.ERRORMESSAGE.FAILEDTOFATCH;
        dispatch(setNarrativeBoardMessage(error));
    }
}